import type { ReactNode } from 'react';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';

/**
 * The company block at the top of a printed voucher.
 *
 * A voucher is a document that leaves the building, so the name at the top of it has to be the
 * company's — the registered name, its address and its registration number. Before this the cheque
 * voucher printed "myLedger ONE", the name of the software, over an empty company line: nothing on
 * the page said which business issued the cheque.
 *
 * Shared so every voucher's letterhead is the same one. Styles are inline because these components
 * render inside a print surface, where a stylesheet class may or may not follow them.
 */

interface Props {
  /** The document's own title, e.g. "Cheque Record". */
  title: string;
  /** Right-hand block — document number, date, status. */
  children?: ReactNode;
}

const MUTED = '#6b7280';
const INK   = '#111827';
const BRAND = '#1e3a5f';

export const VoucherLetterhead = ({ title, children }: Props) => {
  const { displayName, addressLines, registrationNumber, taxNumber, phone, email } =
    useCompanyProfile();

  // One line rather than four: a voucher header should not push the document itself down the page.
  const contact = [phone, email].filter(Boolean).join('  ·  ');
  const idents  = [
    registrationNumber && `Reg. No. ${registrationNumber}`,
    taxNumber && `Tax No. ${taxNumber}`,
  ].filter(Boolean).join('  ·  ');

  return (
    <div style={{ borderBottom: `2px solid ${BRAND}`, paddingBottom: '12px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
        <div style={{ minWidth: 0 }}>
          {/* The company, not the product — this is the name that answers "who issued this?". */}
          <div style={{ fontSize: '15px', fontWeight: 800, color: INK, lineHeight: 1.25 }}>
            {displayName || '—'}
          </div>

          {addressLines.length > 0 && (
            <div style={{ fontSize: '9.5px', color: MUTED, lineHeight: 1.5, marginTop: '2px' }}>
              {addressLines.map(l => <div key={l}>{l}</div>)}
            </div>
          )}

          {contact && (
            <div style={{ fontSize: '9.5px', color: MUTED, marginTop: '1px' }}>{contact}</div>
          )}

          {idents && (
            <div style={{ fontSize: '9.5px', color: MUTED, marginTop: '1px' }}>{idents}</div>
          )}

          <div style={{
            fontSize: '17px', fontWeight: 800, color: BRAND,
            lineHeight: 1.2, marginTop: '10px',
          }}>
            {title}
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>{children}</div>
      </div>
    </div>
  );
};
